<template>
  <v-dialog v-model="visible" max-width="1200px" scrollable persistent>
    <v-card>
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          <v-icon left small>mdi-school</v-icon>
          {{ titulo }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon small dark @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: 78vh; overflow-y: auto">
        <v-form ref="formRef" lazy-validation @submit.prevent="guardar">
          <v-stepper v-model="pasoActual" flat non-linear class="elevation-0">
            <v-stepper-header class="elevation-0">
              <v-stepper-step :complete="pasoActual > 1" step="1" editable> Datos del Programa </v-stepper-step>
              <v-divider />
              <v-stepper-step :complete="pasoActual > 2" step="2" editable>
                Lista de Costos y Precios de Referencia
              </v-stepper-step>
              <v-divider />
              <v-stepper-step step="3" editable> Módulos </v-stepper-step>
            </v-stepper-header>

            <v-divider class="my-0" />

            <v-stepper-items>
              <!-- ==================== PASO 1: DATOS DEL PROGRAMA ==================== -->
              <v-stepper-content step="1" class="pa-5" translate="fa">
                <!-- Sede -->
                <v-sheet rounded outlined class="pa-4 mb-4">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-map-marker</v-icon>
                    <div style="flex: 1">
                      <div class="text-caption grey--text mb-1">Sede Universidad*</div>
                      <v-select
                        v-model="form.sedeUniversidadId"
                        :items="sedesUniversidades"
                        item-text="nombre"
                        item-value="id"
                        placeholder="Seleccione una sede"
                        outlined
                        dense
                        clearable
                        :rules="[reglas.requerido]"
                        @change="onSedeChange">
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-medium">{{ item.nombre }}</v-list-item-title>
                            <v-list-item-subtitle class="text-uppercase">
                              <small>{{ item.institucion.nombre }}</small>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-select>
                    </div>
                  </div>
                </v-sheet>

                <!-- Datos del Programa -->
                <v-sheet class="pa-4 mb-4 seccion" rounded>
                  <div class="seccion-titulo mb-3">
                    <v-icon small color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                    <span>Datos del Programa</span>
                  </div>

                  <v-row dense>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="form.codigo"
                        label="Código*"
                        outlined
                        dense
                        clearable
                        :rules="[reglas.requerido]"></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="12" md="8">
                      <v-text-field
                        v-model="form.programaNombre"
                        label="Nombre del Programa*"
                        outlined
                        dense
                        clearable
                        :rules="[reglas.requerido]"></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <v-select
                        v-model="form.categoriaUniversidadId"
                        :items="categoriasUniversidades"
                        label="Tipo / Categoría*"
                        outlined
                        dense
                        item-text="nombre"
                        item-value="id"
                        clearable
                        :disabled="categoriasUniversidades.length === 0"
                        :rules="[reglas.requerido]"
                        @change="onCategoriaAreaModalidadChange"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <v-select
                        v-model="form.areaUniversidadId"
                        :items="areasUniversidades"
                        label="Área*"
                        outlined
                        dense
                        item-text="nombre"
                        item-value="id"
                        clearable
                        :disabled="areasUniversidades.length === 0"
                        :rules="[reglas.requerido]"
                        @change="onCategoriaAreaModalidadChange"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <v-select
                        v-model="form.modalidad"
                        :items="modalidadesOpciones"
                        label="Modalidad*"
                        outlined
                        dense
                        clearable
                        :rules="[reglas.requerido]"
                        @change="onCategoriaAreaModalidadChange"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="form.version"
                        label="Versión*"
                        outlined
                        dense
                        clearable
                        :rules="[reglas.requerido]"></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="12" md="8">
                      <v-text-field
                        v-model="form.resolucionAprobacionUniversidad"
                        label="Resolución Aprobación*"
                        outlined
                        dense
                        clearable
                        :rules="[reglas.requerido]"></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="4" class="d-flex align-center">
                      <v-switch
                        v-model="form.esFavorito"
                        color="primary"
                        inset
                        dense
                        class="mt-0 pt-0"
                        label="Es Favorito">
                      </v-switch>
                    </v-col>
                  </v-row>
                </v-sheet>

                <!-- Gestión y Fechas -->
                <v-sheet class="pa-4 mb-4 seccion" rounded>
                  <div class="seccion-titulo mb-3">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                    <span>Gestión y Fechas</span>
                  </div>

                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="form.gestion"
                        :items="gestiones"
                        label="Gestión*"
                        outlined
                        dense
                        item-text="gestion"
                        item-value="gestion"
                        clearable
                        :rules="[reglas.requerido]"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="form.grupo"
                        type="number"
                        min="1"
                        label="Grupo"
                        outlined
                        dense
                        clearable></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-menu
                        v-model="menuFechaInicio"
                        transition="scale-transition"
                        :close-on-content-click="false"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.fechaInicio"
                            label="Fecha Inicio"
                            prepend-inner-icon="mdi-calendar"
                            outlined
                            dense
                            readonly
                            clearable
                            v-bind="attrs"
                            v-on="on"></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.fechaInicio"
                          no-title
                          scrollable
                          @input="menuFechaInicio = false"></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-menu
                        v-model="menuFechaFin"
                        transition="scale-transition"
                        :close-on-content-click="false"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.fechaFin"
                            label="Fecha Fin"
                            prepend-inner-icon="mdi-calendar"
                            outlined
                            dense
                            readonly
                            clearable
                            v-bind="attrs"
                            v-on="on"></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.fechaFin"
                          no-title
                          scrollable
                          @input="menuFechaFin = false"></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-menu
                        v-model="menuFechaInscripcion"
                        transition="scale-transition"
                        :close-on-content-click="false"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.fechaInscripcion"
                            label="Fecha Inscripción"
                            prepend-inner-icon="mdi-calendar"
                            outlined
                            dense
                            readonly
                            clearable
                            v-bind="attrs"
                            v-on="on"></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.fechaInscripcion"
                          no-title
                          scrollable
                          @input="menuFechaInscripcion = false"></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-stepper-content>

              <!-- ==================== PASO 2: LISTA DE COSTOS ==================== -->
              <v-stepper-content step="2" class="pa-5">
                <v-sheet class="pa-4 mb-4 seccion" rounded>
                  <div class="seccion-titulo mb-3">
                    <v-icon small color="primary" class="mr-2">mdi-cash-multiple</v-icon>
                    <span>Lista de Costos Convenio*</span>
                  </div>

                  <v-alert v-if="!puedeConsultarListasCostos" type="warning" dense text class="mb-3">
                    Complete los campos Sede, Categoría, Área y Modalidad en el paso anterior para consultar listas de
                    costos.
                  </v-alert>

                  <template v-else>
                    <vue-select
                      v-model="form.listaCostosConvenioId"
                      class="style-chooser"
                      placeholder="Buscar y seleccionar una Lista de Costos..."
                      :options="listasCostosConvenio"
                      label="nombre"
                      :append-to-body="true"
                      :reduce="(option) => option.id"
                      :filterable="true"
                      clearable
                      :disabled="cargandoListasCostos || listasCostosConvenio.length === 0">
                      <template #option="{ nombre, detalles }">
                        <div class="py-2">
                          <strong class="d-block mb-1">{{ nombre }}</strong>
                          <TablaDetallesListaCostosConvenio :detalles="detalles" />
                        </div>
                      </template>
                      <template #no-options>No se encontraron listas de costos</template>
                    </vue-select>

                    <div v-if="validacionListaCostos" class="v-messages theme--light error--text mt-1">
                      <div class="v-messages__message">{{ validacionListaCostos }}</div>
                    </div>

                    <div v-if="!form.listaCostosConvenioId" class="mt-4 text-center">
                      <v-icon size="48" color="grey" class="mb-2">mdi-file-document-outline</v-icon>
                      <div class="my-2 grey--text">No se ha seleccionado una lista de costos</div>
                    </div>

                    <div v-if="listaCostosConvenioSeleccionada" class="mt-4">
                      <TablaDetallesListaCostosConvenio :detalles="listaCostosConvenioSeleccionada.detalles" />
                    </div>
                  </template>

                  <!-- Plan de pago establecido (solo en edición) -->
                  <template v-if="accion === 'editar' && planPagoUniversidadEstablecido">
                    <v-divider class="my-4" />
                    <div class="seccion-titulo mb-2">
                      <v-icon small color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                      <span>Plan de Pago Establecido</span>
                    </div>
                    <v-chip small color="success" outlined class="mb-3">
                      {{ planPagoUniversidadEstablecido.nombre }}
                    </v-chip>
                    <TablaPlanesPagosConceptosUniversidades
                      v-if="
                        planPagoUniversidadEstablecido.planesPagosConceptosUniversidades &&
                        planPagoUniversidadEstablecido.planesPagosConceptosUniversidades.length
                      "
                      :planesPagosConceptosUniversidades="
                        planPagoUniversidadEstablecido.planesPagosConceptosUniversidades
                      " />
                  </template>
                </v-sheet>

                <!-- Precios de Referencia -->
                <v-sheet class="pa-4 mb-4 seccion" rounded>
                  <div class="d-flex flex-wrap align-center justify-space-between mb-3">
                    <div class="seccion-titulo mb-3">
                      <v-icon small color="primary" class="mr-2">mdi-currency-usd</v-icon>

                      <span>
                        Precios de Referencia
                        <v-chip x-small class="ml-2" color="primary" outlined>
                          {{ preciosTodosLosTipos.length }}
                          {{ preciosTodosLosTipos.length === 1 ? 'precio' : 'precios' }}
                        </v-chip>
                      </span>
                    </div>

                    <div v-if="puedeConsultarPrecios" class="d-flex flex-wrap gap-1 mb-3">
                      <span class="caption grey--text mx-2">Precios basados en:</span>

                      <v-chip small outlined>
                        {{
                          categoriasUniversidades.find((c) => c.id === form.categoriaUniversidadId)?.nombre ||
                          'Categoría no seleccionada'
                        }}
                      </v-chip>

                      <v-chip small outlined>
                        {{
                          areasUniversidades.find((a) => a.id === form.areaUniversidadId)?.nombre ||
                          'Área no seleccionada'
                        }}
                      </v-chip>

                      <v-chip small outlined>{{ modalidadesProgramasUniversidadesLabels[form.modalidad] }}</v-chip>
                    </div>

                    <v-btn
                      v-if="puedeConsultarPrecios"
                      small
                      color="primary"
                      outlined
                      @click="obtenerPreciosTodosLosTipos">
                      <v-icon left small>mdi-refresh</v-icon>
                      Buscar Precios
                    </v-btn>
                  </div>

                  <v-alert v-if="!puedeConsultarPrecios" type="warning" dense text class="mb-0">
                    Complete los campos Sede, Categoría, Área y Modalidad en el paso anterior para consultar precios.
                  </v-alert>

                  <v-alert v-else-if="errorPrecios" type="error" dense text class="mb-0">
                    {{ errorPrecios }}
                  </v-alert>

                  <template v-else>
                    <v-tabs v-model="tabPrecios" dense height="32" class="mb-2">
                      <v-tab class="text-caption">Pivot</v-tab>
                      <v-tab class="text-caption">Lista</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="tabPrecios">
                      <v-tab-item>
                        <TablaPivotPreciosTodosLosTipos :items="preciosTodosLosTipos" :cargando="cargandoPrecios" />
                      </v-tab-item>
                      <v-tab-item>
                        <TablaPreciosTodosLosTipos :items="preciosTodosLosTipos" :cargando="cargandoPrecios" />
                      </v-tab-item>
                    </v-tabs-items>
                  </template>
                </v-sheet>
              </v-stepper-content>

              <!-- ==================== PASO 3: MÓDULOS ==================== -->
              <v-stepper-content step="3" class="pa-5" translate="fa">
                <v-sheet class="pa-4 seccion" rounded>
                  <div class="d-flex flex-wrap align-center justify-space-between mb-3">
                    <div class="seccion-titulo">
                      <v-icon small color="primary" class="mr-2">mdi-view-module</v-icon>
                      <span>Módulos del Programa</span>
                      <v-chip x-small class="ml-2" color="primary" outlined>
                        {{ form.modulosProgramasUniversidades.length }}
                      </v-chip>
                    </div>
                    <v-btn small color="primary" outlined @click="agregarModulo">
                      <v-icon small left>mdi-plus</v-icon>
                      Agregar
                    </v-btn>
                  </div>

                  <v-alert v-if="form.modulosProgramasUniversidades.length === 0" type="info" text dense class="mb-0">
                    No se han agregado módulos. Haga clic en "Agregar" para comenzar.
                  </v-alert>

                  <draggable
                    v-model="form.modulosProgramasUniversidades"
                    handle=".modulo-drag-handle"
                    @end="renumerarModulos">
                    <v-card
                      v-for="(modulo, index) in form.modulosProgramasUniversidades"
                      :key="index"
                      outlined
                      class="modulo-card mb-3">
                      <div class="modulo-header">
                        <div class="d-flex align-center">
                          <v-icon small class="modulo-drag-handle mr-2">mdi-drag</v-icon>
                          <v-avatar size="26" color="primary" class="mr-2">
                            <span class="white--text caption font-weight-bold">{{ modulo.numero }}</span>
                          </v-avatar>
                        </div>
                        <v-btn icon x-small color="red" @click="eliminarModulo(index)" title="Eliminar módulo">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>

                      <div class="modulo-body">
                        <v-row dense>
                          <v-col cols="12" sm="12" md="8" lg="9">
                            <v-text-field
                              v-model="modulo.nombre"
                              label="Nombre del Módulo*"
                              outlined
                              dense
                              clearable
                              :rules="[reglas.requerido]"
                              placeholder="Ej: Módulo de Introducción"></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="12" md="4" lg="3">
                            <v-select
                              v-model="modulo.tipoAvance"
                              :items="tipoAvanceOpciones"
                              item-text="text"
                              item-value="value"
                              label="Tipo de Avance*"
                              outlined
                              dense
                              :rules="[reglas.requerido]"></v-select>
                          </v-col>

                          <v-col cols="12" sm="6" lg="3">
                            <v-text-field
                              v-model.number="modulo.cargaHorariaPresencial"
                              type="number"
                              min="0"
                              label="Hrs. Presencial*"
                              outlined
                              dense
                              prepend-inner-icon="mdi-clock-outline"
                              :rules="[reglas.requerido, reglas.numeroPositivo]"
                              @input="actualizarNumeroCreditos(modulo)"></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="6" lg="3">
                            <v-text-field
                              v-model.number="modulo.cargaHorariaNoPresencial"
                              type="number"
                              min="0"
                              label="Hrs. No Presencial*"
                              outlined
                              dense
                              prepend-inner-icon="mdi-clock-outline"
                              :rules="[reglas.requerido, reglas.numeroPositivo]"
                              @input="actualizarNumeroCreditos(modulo)"></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="6" lg="3">
                            <v-text-field
                              :value="horasTotalesModulo(modulo)"
                              type="number"
                              label="Horas Totales"
                              outlined
                              dense
                              readonly
                              prepend-inner-icon="mdi-sigma"></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="6" lg="3">
                            <v-text-field
                              :value="(modulo.cargaHorariaPresencial + modulo.cargaHorariaNoPresencial) / 40"
                              type="number"
                              label="N° Créditos"
                              outlined
                              dense
                              readonly
                              prepend-inner-icon="mdi-counter"
                              :rules="[
                                (v) => Number.isInteger(Number(v)) || 'La suma de horas debe ser múltiplo de 40',
                              ]"></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="6" lg="3">
                            <v-menu
                              v-model="modulo.menuFechaInicio"
                              transition="scale-transition"
                              :close-on-content-click="false"
                              offset-y
                              min-width="auto">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="modulo.fechaInicio"
                                  label="Fecha Inicio"
                                  prepend-inner-icon="mdi-calendar"
                                  outlined
                                  dense
                                  readonly
                                  clearable
                                  v-bind="attrs"
                                  v-on="on"></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="modulo.fechaInicio"
                                no-title
                                scrollable
                                @input="modulo.menuFechaInicio = false"></v-date-picker>
                            </v-menu>
                          </v-col>

                          <v-col cols="12" sm="6" lg="3">
                            <v-menu
                              v-model="modulo.menuFechaFin"
                              transition="scale-transition"
                              :close-on-content-click="false"
                              offset-y
                              min-width="auto">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="modulo.fechaFin"
                                  label="Fecha Fin"
                                  prepend-inner-icon="mdi-calendar"
                                  outlined
                                  dense
                                  readonly
                                  clearable
                                  v-bind="attrs"
                                  v-on="on"></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="modulo.fechaFin"
                                no-title
                                scrollable
                                @input="modulo.menuFechaFin = false"></v-date-picker>
                            </v-menu>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>
                  </draggable>

                  <v-sheet
                    v-if="form.modulosProgramasUniversidades.length > 0"
                    outlined
                    rounded
                    class="totales-modulos pa-3 mt-3">
                    <v-row dense>
                      <v-col cols="12" sm="6" lg="3">
                        <div class="campo-total">
                          <div class="campo-total-label">Total Hrs. Presencial</div>
                          <div class="campo-total-valor">{{ totalesModulos.presencial }}</div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" lg="3">
                        <div class="campo-total">
                          <div class="campo-total-label">Total Hrs. No Presencial</div>
                          <div class="campo-total-valor">{{ totalesModulos.noPresencial }}</div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" lg="3">
                        <div class="campo-total">
                          <div class="campo-total-label">Total Horas</div>
                          <div class="campo-total-valor">
                            {{ totalesModulos.presencial + totalesModulos.noPresencial }}
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" lg="3">
                        <div class="campo-total">
                          <div class="campo-total-label">Total Créditos</div>
                          <div class="campo-total-valor">{{ totalesModulos.creditos }}</div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-sheet>
                </v-sheet>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-5 py-3 flex-wrap">
        <v-btn class="ma-1" text :disabled="pasoActual <= 1 || enviando" @click="pasoActual--">
          <v-icon left small>mdi-arrow-left</v-icon>
          Anterior
        </v-btn>
        <v-btn class="ma-1" color="secondary" :disabled="pasoActual >= 3 || enviando" @click="pasoActual++">
          Siguiente
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>

        <v-spacer />

        <v-btn class="ma-1" text :disabled="enviando" @click="cerrar">Cancelar</v-btn>
        <v-btn class="ma-1" color="primary" :disabled="enviando" :loading="enviando" @click="guardar">
          <v-icon left small>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import 'vue-select/dist/vue-select.css';
import ProgramaUniversidadV2Service from '../../services/programa-universidad-v2.service';
import SedeUniversidadService from '../../services/sede-universidad.service';
import GestionService from '../../services/gestion.service';
import AreaUniversidadService from '../../services/area-universidad.service';
import CategoriaUniversidadService from '../../services/categoria-universidad.service';
import ListaCostosConvenioService from '../../services/lista-costos-convenio.service';
import ListaPrecioService from '../../services/lista-precio.service';
import TablaDetallesListaCostosConvenio from '../TablaDetallesListaCostosConvenio.vue';
import TablaPlanesPagosConceptosUniversidades from '../TablaPlanesPagosConceptosUniversidades.vue';
import TablaPreciosTodosLosTipos from '../ListaPrecio/TablaPreciosTodosLosTipos.vue';
import TablaPivotPreciosTodosLosTipos from '../ListaPrecio/TablaPivotPreciosTodosLosTipos.vue';
import {
  ModalidadesProgramasUniversidadesLabels,
  modalidadesProgramasUniversidadesOpciones,
  MODALIDAD_MAP,
  tiposAvancesOpciones,
} from '../../utils/constantes';
import { formatearErrores } from '../../utils/funciones';
import draggable from 'vuedraggable';

export default {
  name: 'ModalFormularioProgramaUniversidadV2',
  components: {
    TablaDetallesListaCostosConvenio,
    TablaPlanesPagosConceptosUniversidades,
    TablaPreciosTodosLosTipos,
    TablaPivotPreciosTodosLosTipos,
    draggable,
  },
  data() {
    return {
      visible: false,
      enviando: false,
      accion: null,
      pasoActual: 1,
      intentoGuardar: false,
      form: this.crearFormVacio(),
      sedesUniversidades: [],
      areasUniversidades: [],
      categoriasUniversidades: [],
      gestiones: [],
      listasCostosConvenio: [],
      cargandoListasCostos: false,
      planPagoUniversidadEstablecido: null,
      menuFechaInicio: false,
      menuFechaFin: false,
      menuFechaInscripcion: false,
      preciosTodosLosTipos: [],
      cargandoPrecios: false,
      errorPrecios: null,
      tabPrecios: 0,
      modalidadesProgramasUniversidadesLabels: ModalidadesProgramasUniversidadesLabels,
      modalidadesOpciones: modalidadesProgramasUniversidadesOpciones,
      tipoAvanceOpciones: tiposAvancesOpciones,
      reglas: {
        requerido: (v) => !!v || v === 0 || 'Campo requerido',
        numeroPositivo: (v) => v === null || v === '' || Number(v) > 0 || 'Debe ser mayor a 0',
      },
    };
  },
  watch: {
    pasoActual(newVal) {
      if (newVal == 2) {
        this.obtenerListasCostosConvenio();
        this.obtenerPreciosTodosLosTipos();
      }
    },
  },
  computed: {
    titulo() {
      return this.accion === 'editar' ? 'EDITAR PROGRAMA UNIVERSIDAD' : 'REGISTRAR PROGRAMA UNIVERSIDAD';
    },
    validacionListaCostos() {
      if (this.intentoGuardar && this.accion !== 'editar' && !this.form.listaCostosConvenioId) {
        return 'Debe seleccionar una lista de costos';
      }
      return null;
    },
    puedeConsultarPrecios() {
      return !!(
        this.form.sedeUniversidadId &&
        this.form.categoriaUniversidadId &&
        this.form.areaUniversidadId &&
        this.form.modalidad
      );
    },
    puedeConsultarListasCostos() {
      return !!(
        this.form.sedeUniversidadId &&
        this.form.categoriaUniversidadId &&
        this.form.areaUniversidadId &&
        this.form.modalidad
      );
    },
    listaCostosConvenioSeleccionada() {
      return this.listasCostosConvenio.find((l) => l.id === this.form.listaCostosConvenioId) || null;
    },
    totalesModulos() {
      return this.form.modulosProgramasUniversidades.reduce(
        (acc, m) => {
          acc.presencial += Number(m.cargaHorariaPresencial || 0);
          acc.noPresencial += Number(m.cargaHorariaNoPresencial || 0);
          acc.creditos += Number(m.numeroCreditos || 0);
          return acc;
        },
        { presencial: 0, noPresencial: 0, creditos: 0 },
      );
    },
  },
  methods: {
    crearFormVacio() {
      return {
        id: null,
        sedeUniversidadId: null,
        codigo: null,
        programaNombre: null,
        version: null,
        modalidad: null,
        areaUniversidadId: null,
        categoriaUniversidadId: null,
        gestion: null,
        grupo: null,
        resolucionAprobacionUniversidad: null,
        esFavorito: false,
        fechaInicio: null,
        fechaFin: null,
        fechaInscripcion: null,
        listaCostosConvenioId: null,
        modulosProgramasUniversidades: [],
      };
    },
    poblarFormEditar(data) {
      this.planPagoUniversidadEstablecido = data.planPagoUniversidad || null;
      this.form = {
        id: data.id,
        sedeUniversidadId: data.sedeUniversidadId,
        codigo: data.codigo,
        programaNombre: data.programaNombre,
        version: data.version,
        modalidad: data.modalidad,
        areaUniversidadId: data.areaUniversidadId,
        categoriaUniversidadId: data.categoriaUniversidadId,
        gestion: data.gestion,
        grupo: data.grupo,
        resolucionAprobacionUniversidad: data.resolucionAprobacionUniversidad,
        esFavorito: !!data.esFavorito,
        fechaInicio: data.fechaInicio,
        fechaFin: data.fechaFin,
        fechaInscripcion: data.fechaInscripcion,
        listaCostosConvenioId: null,
        modulosProgramasUniversidades: (data.modulosProgramasUniversidades || [])
          .map((modulo) => ({
            id: modulo.id,
            nombre: modulo.nombre,
            numero: modulo.numero,
            cargaHorariaPresencial: modulo.cargaHorariaPresencial,
            cargaHorariaNoPresencial: modulo.cargaHorariaNoPresencial,
            numeroCreditos: (modulo.cargaHorariaPresencial + modulo.cargaHorariaNoPresencial) / 40,
            docenteId: modulo.docenteId,
            tipoAvance: modulo.tipoAvance,
            fechaInicio: modulo.fechaInicio,
            fechaFin: modulo.fechaFin,
            menuFechaInicio: false,
            menuFechaFin: false,
          }))
          .sort((a, b) => a.numero - b.numero),
      };
    },
    async abrir(accion, data = null) {
      this.accion = accion;
      this.pasoActual = 1;
      this.intentoGuardar = false;
      this.areasUniversidades = [];
      this.categoriasUniversidades = [];
      this.listasCostosConvenio = [];
      this.planPagoUniversidadEstablecido = null;
      this.visible = true;

      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.resetValidation();
        }
      });

      await Promise.all([this.obtenerSedesUniversidades(), this.obtenerGestiones()]);

      if (accion === 'editar' && data) {
        if (data.sedeUniversidadId) {
          const sede = this.sedesUniversidades.find((s) => s.id === data.sedeUniversidadId);
          if (sede) {
            await Promise.all([
              this.obtenerAreasUniversidades(sede.universidadId),
              this.obtenerCategoriasUniversidades(sede.universidadId),
            ]);
          }
        }
        this.poblarFormEditar(data);
        await this.obtenerListasCostosConvenio();
      } else {
        this.form = this.crearFormVacio();
      }
    },
    cerrar() {
      this.visible = false;
    },
    async obtenerSedesUniversidades() {
      try {
        const { data } = await SedeUniversidadService.findAll({
          params: {
            sedeId: this.$store.state.CARGO.id == 1 ? null : this.$store.state.CARGO.sede.id,
          },
        });
        this.sedesUniversidades = data.datos;
      } catch (error) {
        this.sedesUniversidades = [];
        this.$notify({ group: 'notifications', title: 'Error', text: 'Error al obtener sedes', type: 'error' });
      }
    },
    async obtenerGestiones() {
      try {
        const { data } = await GestionService.findAll();
        this.gestiones = data.datos;
      } catch (error) {
        this.gestiones = [];
        this.$notify({ group: 'notifications', title: 'Error', text: 'Error al obtener gestiones', type: 'error' });
      }
    },
    async onSedeChange(sedeUniversidadId) {
      this.form.areaUniversidadId = null;
      this.form.categoriaUniversidadId = null;
      this.form.listaCostosConvenioId = null;
      this.areasUniversidades = [];
      this.categoriasUniversidades = [];
      this.listasCostosConvenio = [];
      this.preciosTodosLosTipos = [];
      this.errorPrecios = null;

      if (!sedeUniversidadId) return;

      const sede = this.sedesUniversidades.find((s) => s.id === sedeUniversidadId);
      if (!sede) return;

      await Promise.all([
        this.obtenerAreasUniversidades(sede.universidadId),
        this.obtenerCategoriasUniversidades(sede.universidadId),
      ]);
    },
    onCategoriaAreaModalidadChange() {
      this.form.listaCostosConvenioId = null;
      this.listasCostosConvenio = [];
      this.obtenerListasCostosConvenio();
      this.obtenerPreciosTodosLosTipos();
    },
    async obtenerAreasUniversidades(universidadId) {
      try {
        const { data } = await AreaUniversidadService.findAll({ params: { universidadId } });
        this.areasUniversidades = data.datos;
      } catch (error) {
        this.areasUniversidades = [];
        this.$notify({ group: 'notifications', title: 'Error', text: 'Error al obtener áreas', type: 'error' });
      }
    },
    async obtenerCategoriasUniversidades(universidadId) {
      try {
        const { data } = await CategoriaUniversidadService.findAll({ params: { universidadId } });
        this.categoriasUniversidades = data.datos;
      } catch (error) {
        this.categoriasUniversidades = [];
        this.$notify({ group: 'notifications', title: 'Error', text: 'Error al obtener categorías', type: 'error' });
      }
    },
    async obtenerListasCostosConvenio() {
      if (!this.puedeConsultarListasCostos) return;

      const sede = this.sedesUniversidades.find((s) => s.id === this.form.sedeUniversidadId);

      if (!sede) {
        return;
      }

      const modalidad = MODALIDAD_MAP[this.form.modalidad];

      if (!modalidad) {
        return;
      }

      this.cargandoListasCostos = true;
      try {
        const { data } = await ListaCostosConvenioService.consultar({
          params: {
            universidadId: sede.universidadId,
            categoriaUniversidadId: this.form.categoriaUniversidadId,
            areaUniversidadId: this.form.areaUniversidadId,
            modalidad,
          },
        });
        this.listasCostosConvenio = data.datos || [];
      } catch (error) {
        this.listasCostosConvenio = [];
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: 'Error al obtener listas de costos',
          type: 'error',
        });
      } finally {
        this.cargandoListasCostos = false;
      }
    },
    async obtenerPreciosTodosLosTipos() {
      if (!this.puedeConsultarPrecios) {
        return;
      }

      this.preciosTodosLosTipos = [];

      const sede = this.sedesUniversidades.find((s) => s.id === this.form.sedeUniversidadId);
      if (!sede) return;

      const categoria = this.categoriasUniversidades.find((c) => c.id === this.form.categoriaUniversidadId);
      const area = this.areasUniversidades.find((a) => a.id === this.form.areaUniversidadId);
      if (!categoria || !area) return;

      this.cargandoPrecios = true;
      this.errorPrecios = null;

      try {
        const { data } = await ListaPrecioService.consultarPreciosTodosLosTipos({
          params: {
            universidadId: sede.universidadId,
            categoria: categoria.nombre,
            area: area.nombre,
            modalidad: this.form.modalidad,
          },
        });
        this.preciosTodosLosTipos = data.datos || [];
      } catch (error) {
        this.preciosTodosLosTipos = [];
        this.errorPrecios = 'Error al consultar precios. Intente nuevamente.';
      } finally {
        this.cargandoPrecios = false;
      }
    },
    agregarModulo() {
      const numero = this.form.modulosProgramasUniversidades.length + 1;
      this.form.modulosProgramasUniversidades.push({
        id: null,
        nombre: null,
        numero,
        cargaHorariaPresencial: 0,
        cargaHorariaNoPresencial: 0,
        numeroCreditos: 0,
        docenteId: null,
        tipoAvance: null,
        fechaInicio: null,
        fechaFin: null,
        menuFechaInicio: false,
        menuFechaFin: false,
      });
    },
    eliminarModulo(index) {
      this.form.modulosProgramasUniversidades.splice(index, 1);
      this.renumerarModulos();
    },
    renumerarModulos() {
      this.form.modulosProgramasUniversidades.forEach((modulo, i) => {
        modulo.numero = i + 1;
      });
    },
    horasTotalesModulo(modulo) {
      return Number(modulo.cargaHorariaPresencial || 0) + Number(modulo.cargaHorariaNoPresencial || 0);
    },
    actualizarNumeroCreditos(modulo) {
      modulo.numeroCreditos =
        (Number(modulo.cargaHorariaPresencial || 0) + Number(modulo.cargaHorariaNoPresencial || 0)) / 40;
    },
    async guardar() {
      if (this.enviando) return;

      this.intentoGuardar = true;
      const formValido = this.$refs.formRef.validate();
      const listaCostosValida = this.accion === 'editar' || !!this.form.listaCostosConvenioId;
      const modulosValidos = this.form.modulosProgramasUniversidades.length > 0;

      if (!formValido || !listaCostosValida || !modulosValidos) {
        this.$notify({
          group: 'notifications',
          title: 'Error de Validación',
          text: 'Por favor, complete todos los campos requeridos antes de guardar.',
          type: 'error',
        });

        if (!formValido) {
          this.pasoActual = 1;

          return;
        }

        if (!listaCostosValida) {
          this.pasoActual = 2;

          return;
        }

        if (!modulosValidos) {
          this.pasoActual = 3;

          return;
        }
      }

      this.enviando = true;

      try {
        const payload = {
          ...this.form,
          modulosProgramasUniversidades: this.form.modulosProgramasUniversidades.map((modulo) => ({
            id: modulo.id,
            nombre: modulo.nombre,
            numero: modulo.numero,
            cargaHorariaPresencial: modulo.cargaHorariaPresencial,
            cargaHorariaNoPresencial: modulo.cargaHorariaNoPresencial,
            numeroCreditos: modulo.numeroCreditos,
            docenteId: modulo.docenteId,
            tipoAvance: modulo.tipoAvance,
            fechaInicio: modulo.fechaInicio,
            fechaFin: modulo.fechaFin,
          })),
        };

        const response =
          this.accion === 'editar'
            ? await ProgramaUniversidadV2Service.update(this.form.id, payload)
            : await ProgramaUniversidadV2Service.create(payload);

        this.$notify({
          group: 'notifications',
          title: 'PROGRAMA UNIVERSIDAD',
          text: response.data.mensaje,
          type: 'success',
        });

        this.$emit('actualizarListado');
        this.cerrar();
      } catch (error) {
        const data = error?.response?.data;
        const errores = data?.errores;
        const mensaje = data?.mensaje;
        const erroresFormateados = formatearErrores(errores);

        this.$notify({
          group: 'notifications',
          title: mensaje || 'ERROR EN LA PETICIÓN',
          text: erroresFormateados,
          type: 'error',
        });
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.seccion {
  border-left: 3px solid #1976d2;
  background-color: #fafafa;
}

.seccion-titulo {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.modulo-card {
  border-color: #e0e0e0 !important;
  transition: border-color 0.2s;
}

.modulo-card:hover {
  border-color: #90caf9 !important;
}

.modulo-drag-handle {
  cursor: grab;
  color: #9e9e9e;
}

.modulo-drag-handle:active {
  cursor: grabbing;
}

.modulo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background-color: #eceff1;
  border-bottom: 1px solid #e0e0e0;
}

.modulo-body {
  padding: 12px;
}

::v-deep .v-stepper__header {
  box-shadow: none;
}

::v-deep .v-select.v-text-field--outlined:not(.v-text-field--single-line).v-input--dense .v-select__selections {
  padding: 0px 0;
}

.totales-modulos {
  background-color: #e3f2fd;
  border-color: #90caf9 !important;
}

.campo-total {
  text-align: center;
  padding: 4px 0;
}

.campo-total-label {
  font-size: 0.75rem;
  color: #1565c0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.campo-total-valor {
  font-size: 1.25rem;
  color: #0d47a1;
  font-weight: 700;
  margin-top: 2px;
}

::v-deep .v-text-field .v-input__control .v-input__slot {
  min-height: auto !important;
  display: flex !important;
  align-items: center !important;
}

::v-deep .v-text-field--outlined.v-input--dense .v-label {
  top: 5px;
}

::v-deep .v-text-field .v-label {
  font-weight: 400;
}
</style>
